import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslocoModule } from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';

@Component({
  selector: 'anihis-lab-hemato-bio-dog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
  ],
  templateUrl: './lab-hemato-bio-dog.component.html',
  styleUrls: ['./lab-hemato-bio-dog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabHematoBioDogComponent
  extends FormBaseComponent
  implements OnInit
{
  @Output() formChange = new EventEmitter<any>();
  leftParams: Array<{ labelKey: string; control: string }> = [];
  rightParams: Array<{ labelKey: string; control: string }> = [];
  unitMap: Record<string, string> = {};
  refMap: Record<string, string> = {};

  constructor() {
    super();
    this.form = this.fb.group({
      // Hematology
      wbc: new FormControl(''),
      lymphCount: new FormControl(''),
      monCount: new FormControl(''),
      granCount: new FormControl(''),
      lymphPercent: new FormControl(''),
      monPercent: new FormControl(''),
      granPercent: new FormControl(''),
      rbc: new FormControl(''),
      hgb: new FormControl(''),
      hct: new FormControl(''),
      mcv: new FormControl(''),
      mch: new FormControl(''),
      mchc: new FormControl(''),
      rdw: new FormControl(''),
      plt: new FormControl(''),
      mpv: new FormControl(''),
      pdw: new FormControl(''),
      pct: new FormControl(''),
      eos: new FormControl(''),
      // Biochemistry
      alt: new FormControl(''),
      ast: new FormControl(''),
      alp: new FormControl(''),
      ggt: new FormControl(''),
      ck: new FormControl(''),
      ldh: new FormControl(''),
      amylase: new FormControl(''),
      glucose: new FormControl(''),
      totalProteins: new FormControl(''),
      albumin: new FormControl(''),
      globulins: new FormControl(''),
      agRatio: new FormControl(''),
      urea: new FormControl(''),
      creatinine: new FormControl(''),
      ureaCreatinineRatio: new FormControl(''),
      bilirubin: new FormControl(''),
      triglycerides: new FormControl(''),
      cholesterol: new FormControl(''),
      calcium: new FormControl(''),
      phosphorus: new FormControl(''),
      potassium: new FormControl(''),
      sodium: new FormControl(''),
      chloride: new FormControl(''),
      magnesium: new FormControl(''),
      iron: new FormControl(''),
      // Blood smear
      bloodSmear: new FormControl(''),
      veterinarianName: new FormControl(''),
    });

    this.leftParams = [
      { labelKey: 'WBC', control: 'wbc' },
      { labelKey: 'Lymph#', control: 'lymphCount' },
      { labelKey: 'Mon#', control: 'monCount' },
      { labelKey: 'Gran#', control: 'granCount' },
      { labelKey: 'Lymph%', control: 'lymphPercent' },
      { labelKey: 'Mon%', control: 'monPercent' },
      { labelKey: 'Gran%', control: 'granPercent' },
      { labelKey: 'RBC', control: 'rbc' },
      { labelKey: 'HGB', control: 'hgb' },
      { labelKey: 'HCT', control: 'hct' },
      { labelKey: 'MCV', control: 'mcv' },
      { labelKey: 'MCH', control: 'mch' },
      { labelKey: 'MCHC', control: 'mchc' },
      { labelKey: 'RDW', control: 'rdw' },
      { labelKey: 'PLT', control: 'plt' },
      { labelKey: 'MPV', control: 'mpv' },
      { labelKey: 'PDW', control: 'pdw' },
      { labelKey: 'PCT', control: 'pct' },
      { labelKey: 'EOS', control: 'eos' },
    ];

    this.rightParams = [
      { labelKey: 'ALT', control: 'alt' },
      { labelKey: 'AST', control: 'ast' },
      { labelKey: 'ALP', control: 'alp' },
      { labelKey: 'GGT', control: 'ggt' },
      { labelKey: 'CK', control: 'ck' },
      { labelKey: 'LDH', control: 'ldh' },
      { labelKey: 'Alfa-amylaza', control: 'amylase' },
      { labelKey: 'Glukoza', control: 'glucose' },
      { labelKey: 'Uk.proteini', control: 'totalProteins' },
      { labelKey: 'Albumini', control: 'albumin' },
      { labelKey: 'Globulini', control: 'globulins' },
      { labelKey: 'Alb/glob', control: 'agRatio' },
      { labelKey: 'Urea', control: 'urea' },
      { labelKey: 'Kreatinin', control: 'creatinine' },
      { labelKey: 'Urea/ Kreatinin', control: 'ureaCreatinineRatio' },
      { labelKey: 'Uk.bilirubin', control: 'bilirubin' },
      { labelKey: 'Trigliceridi', control: 'triglycerides' },
      { labelKey: 'Holesterol', control: 'cholesterol' },
      { labelKey: 'Kalcijum', control: 'calcium' },
      { labelKey: 'Fosfor', control: 'phosphorus' },
      { labelKey: 'Kalijum', control: 'potassium' },
      { labelKey: 'Natrijum', control: 'sodium' },
      { labelKey: 'Hloridi', control: 'chloride' },
      { labelKey: 'Magnezijum', control: 'magnesium' },
      { labelKey: 'Gvožđe', control: 'iron' },
    ];

    this.unitMap = {
      // Hematology
      wbc: 'x10^9/L',
      lymphCount: 'x10^9/L',
      monCount: 'x10^9/L',
      granCount: 'x10^9/L',
      lymphPercent: '%',
      monPercent: '%',
      granPercent: '%',
      rbc: 'x10^9/L',
      hgb: 'g/L',
      hct: '%',
      mcv: 'fL',
      mch: 'pg',
      mchc: 'g/L',
      rdw: '%',
      plt: 'x10^9/L',
      mpv: 'fL',
      pdw: '',
      pct: '%',
      eos: '%',
      // Biochemistry
      alt: 'IU/L',
      ast: 'IU/L',
      alp: 'IU/L',
      ggt: 'IU/L',
      ck: 'IU/L',
      ldh: 'IU/L',
      amylase: 'IU/L',
      glucose: 'mmol/L',
      totalProteins: 'g/L',
      albumin: 'g/L',
      globulins: 'g/L',
      agRatio: '',
      urea: 'mmol/L',
      creatinine: 'mmol/L',
      ureaCreatinineRatio: '',
      bilirubin: 'μmol/L',
      triglycerides: 'mmol/L',
      cholesterol: 'mmol/L',
      calcium: 'mmol/L',
      phosphorus: 'mmol/L',
      potassium: 'mmol/L',
      sodium: 'mmol/L',
      chloride: 'mmol/L',
      magnesium: 'mmol/L',
      iron: 'μmol/L',
    };

    this.refMap = {
      // Hematology (dog)
      wbc: '6.0 - 17.0',
      lymphCount: '0.8 - 5.1',
      monCount: '0.0 - 1.8',
      granCount: '4.0 - 12.6',
      lymphPercent: '12.0 - 30.0',
      monPercent: '2.0 - 9.0',
      granPercent: '60.0 - 83.0',
      rbc: '5.5 - 8.5',
      hgb: '110 - 190',
      hct: '39.0 - 56.0',
      mcv: '62.0 - 72.0',
      mch: '20.0 - 25.0',
      mchc: '300 - 380',
      rdw: '11.0 - 15.5',
      plt: '117 - 460',
      mpv: '7.0 - 12.9',
      pdw: '',
      pct: '',
      eos: '',
      // Biochemistry (dog)
      alt: '0.0 - 57.3',
      ast: '0.0 - 48.5',
      alp: '10.6 - 100.7',
      ggt: '1.0 - 9.7',
      ck: '13.7 - 119.7',
      ldh: '24.1 - 219.2',
      amylase: '269.5 - 1980',
      glucose: '3.0 - 6.6',
      totalProteins: '54 - 75',
      albumin: '25 - 44',
      globulins: '21 - 37',
      agRatio: '0.8 - 2.0',
      urea: '3.1 - 9.3',
      creatinine: '35 - 106',
      ureaCreatinineRatio: '40 - 100',
      bilirubin: '1.7 - 10.0',
      triglycerides: '0.0 - 1.1',
      cholesterol: '3.2 - 6.6',
      calcium: '2.3 - 3.0',
      phosphorus: '1.0 - 2.0',
      potassium: '3.8 - 5.6',
      sodium: '140 - 155',
      chloride: '96 - 113',
      magnesium: '0.6 - 1.3',
      iron: '18 - 36',
    };
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.subs.sink = this.form.valueChanges.subscribe((v) =>
      this.formChange.emit(v),
    );
  }
}
